/*
 * Free-text search over the schedule list.
 *
 * The third of these, after the spare parts register and the work order list. This page
 * was the one left without one, and what stood in its place was an asset dropdown — a filter that
 * could only answer one question, in a control twice the height of everything beside it.
 *
 * Pure, in the shape of workOrderSearch.js and sparePartSearch.js, so the rules can be tested
 * without rendering a grid. The matching rule is shared with both boxes; see
 * features/cmms/cmmsSearch.js.
 *
 * Client-side. The Active dropdown above it is not — it goes into the query key and is answered by
 * the API. That dropdown changes what is fetched; this changes what is shown.
 */
import { filterBySearchText } from "../cmms/cmmsSearch";

/*
 * The row as it reads on screen, lower-cased.
 *
 * The asset is an id, so it is resolved through the same map the grid's own column resolves it
 * with — otherwise "pump" would find nothing while the grid plainly shows Pump P-12. That map is
 * a separate query from the schedules, so it can be empty on the first paint; it is cached for
 * five minutes, which makes that a first-visit flicker rather than a state anyone searches in.
 * The blank is skipped rather than written in as "undefined".
 *
 * The tool name needs no resolving — the service sends it with the row, and it is blank on an
 * asset schedule.
 *
 * Four fields, not seven. Basis, frequency and next run date are all on the grid and none of them
 * are searched: the first two are enum labels the frequency column assembles through three
 * branches, and duplicating that here would be a second copy to keep in step for the sake of
 * finding "monthly" — which the column sorts by anyway. The active flag has its own dropdown, and
 * the id is hidden by default for the same reason the work order list leaves its own out: folding
 * a bare number in makes "12" match every row whose id merely contains it.
 */
export const getRecurringScheduleSearchText = (schedule, context) => {
  const { assetNodeNameById } = context ?? {};

  return [
    schedule?.title,
    schedule?.description,
    assetNodeNameById?.get(schedule?.assetNodeId),
    schedule?.toolName,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

/*
 * The schedules matching every word of the query, in any field and any order — so "pump monthly"
 * is not needed to find the pump's schedule, but "pump seal" works whichever field each word is
 * in. See cmmsSearch.js for why that beats a single substring match.
 *
 * `context` carries the asset name map. Passed per call rather than closed over because the map
 * changes as the tree query resolves.
 */
export const filterRecurringSchedules = (schedules, query, context) =>
  filterBySearchText(schedules, query, (row) => getRecurringScheduleSearchText(row, context));
